import { supabase } from "../supabase";
import type { Booking, BookingStatus } from "../types";

interface BookingRow {
  id: string;
  school_id: string;
  lesson_id: string | null;
  student_id: string | null;
  instructor_id: string | null;
  aircraft_id: string | null;
  booking_date: string;
  start_time: string;
  end_time: string;
  status: BookingStatus;
  ready: boolean;
  notes: string;
}

const COLS =
  "id, school_id, lesson_id, student_id, instructor_id, aircraft_id, booking_date, start_time, end_time, status, ready, notes";

function trimSeconds(t: string): string {
  return t.length > 5 ? t.slice(0, 5) : t;
}

function mapBooking(r: BookingRow): Booking {
  return {
    id: r.id,
    schoolId: r.school_id,
    lessonId: r.lesson_id ?? "",
    studentId: r.student_id,
    instructorId: r.instructor_id,
    aircraftId: r.aircraft_id,
    start: trimSeconds(r.start_time),
    end: trimSeconds(r.end_time),
    status: r.status,
    ready: r.ready,
    notes: r.notes ?? "",
  };
}

export async function listForDate(schoolId: string, date: string): Promise<Booking[]> {
  const { data, error } = await supabase
    .from("bookings")
    .select(COLS)
    .eq("school_id", schoolId)
    .eq("booking_date", date)
    .order("start_time", { ascending: true });
  if (error) throw error;
  return (data as BookingRow[]).map(mapBooking);
}

export interface BookingInput {
  id?: string;
  schoolId: string;
  lessonId: string;
  studentId: string | null;
  instructorId: string | null;
  aircraftId: string | null;
  bookingDate: string;
  start: string;
  end: string;
  status: BookingStatus;
  ready: boolean;
  notes: string;
}

export async function upsert(b: BookingInput): Promise<Booking> {
  const row = {
    id: b.id,
    school_id: b.schoolId,
    lesson_id: b.lessonId,
    student_id: b.studentId,
    instructor_id: b.instructorId,
    aircraft_id: b.aircraftId,
    booking_date: b.bookingDate,
    start_time: b.start,
    end_time: b.end,
    status: b.status,
    ready: b.ready,
    notes: b.notes,
  };
  const { data, error } = await supabase
    .from("bookings")
    .upsert(row, { onConflict: "id" })
    .select(COLS)
    .single();
  if (error) throw error;
  return mapBooking(data as BookingRow);
}

export async function setStatus(
  id: string,
  status: BookingStatus,
  ready?: boolean,
): Promise<void> {
  const patch: { status: BookingStatus; ready?: boolean } = { status };
  if (ready !== undefined) patch.ready = ready;
  const { error } = await supabase.from("bookings").update(patch).eq("id", id);
  if (error) throw error;
}

export async function cancel(id: string): Promise<void> {
  const { error } = await supabase
    .from("bookings")
    .update({ status: "canceled" })
    .eq("id", id);
  if (error) throw error;
}
